// React import not needed with React 19+ JSX transform
import { TimePicker } from '@/components/ui/time-picker';
import type { FieldConfig } from '../types';

interface TimeFieldProps<T> {
  field: FieldConfig<T>;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/**
 * A wall-clock time input (`HH:mm`), added for the CRM facility tour (issue
 * #319).
 *
 * The value is a bare `HH:mm` STRING and carries no timezone at all: the caller
 * combines it with the companion date field into ONE instant in the
 * ORGANISATION's timezone (`combineDateAndTimeInOrgTimezone`). Rendering the
 * time as a `Date` here would silently interpret it in the browser's zone.
 *
 * Renders the shared `TimePicker` (two themed segments), not a native
 * `<input type="time">`: the native control paints the browser's own clock
 * glyph, which ignores our theme and is invisible in dark mode.
 */
export function TimeField<T>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: TimeFieldProps<T>) {
  return (
    <TimePicker
      id={id}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={field.required || undefined}
    />
  );
}
